import type { MatrixClient } from 'matrix-js-sdk';
import type { MsgType, RoomMessageEventContent } from 'matrix-js-sdk/lib/types';
import { getClaims, getTokens } from './authStore';
import type { NotificationType } from './matrixClient';

export const matrixTokenName = 'matrixToken';

export type TestForSufficientBBBSlotsInput = {
  id: string;
  maxAttendees: number;
  start: string;
  end: string;
  until?: string;
  organization: string;
};

export interface BackendApiInterface {
  findConference: (conference: string) => Promise<Response>;
  startConference: (conference: string) => Promise<Response>;
  endConference: (conference: string) => Promise<Response>;
  joinConference: (conference: string, params: JoinParams) => Promise<Response>;
  getMatrixToken: (force?: boolean) => Promise<MatrixTokenType | null>;
  clearMatrixTokens: () => Promise<void>;
  createMatrixDm: (uuid: string) => Promise<Response>;
  redactMatrixEvent: (roomId: string, eventId: string) => Promise<Response>;
  notify: (
    message: Partial<NotificationType>,
    matrixInfos?: { roomId: string; groupName: string; client: MatrixClient },
  ) => Promise<Response>;

  fetch(url: string, options?: RequestInit): Promise<Response>;

  post(url: string, body: string, options?: RequestInit): Promise<Response>;

  put(url: string, body: string, options?: RequestInit): Promise<Response>;

  testForSufficientBBBSlots(input: TestForSufficientBBBSlotsInput): Promise<
    {
      [uuid: string]: number;
    }[]
  >;
}

export type MatrixTokenType = {
  access_token: string;
  user_id: string;
  home_server: string;
  expires_at: number;
};

export type JoinParams = {
  displayName: string;
  email?: string;
  profileUuid?: string;
  moderator?: boolean;
};

export const restApiPath = () => import.meta.env.VITE_APP_BACKEND_URI + import.meta.env.VITE_APP_BACKEND_REST_PATH;

export const backendApi: BackendApiInterface = {
  async fetch(url: string, options?: RequestInit): Promise<Response> {
    const tokens = await getTokens();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: tokens ? `Bearer ${tokens.access_token}` : '',
    };
    const opts: RequestInit = {
      method: 'GET',
      headers,
    };
    return fetch(restApiPath() + url, { ...opts, ...options });
  },
  async post(url: string, body, options?: RequestInit): Promise<Response> {
    const tokens = await getTokens();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: tokens ? `Bearer ${tokens.access_token}` : '',
    };
    const opts: RequestInit = {
      method: 'POST',
      headers,
      body,
    };
    return fetch(restApiPath() + url, { ...options, ...opts });
  },

  async put(url: string, body, options?: RequestInit): Promise<Response> {
    const tokens = await getTokens();
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      Authorization: tokens ? `Bearer ${tokens.access_token}` : '',
    };
    const opts: RequestInit = {
      method: 'PUT',
      headers,
      body,
    };
    return fetch(restApiPath() + url, { ...options, ...opts });
  },

  async findConference(conference: string) {
    return this.fetch(`/calendar/meeting/${conference}`);
  },
  async endConference(conference: string): Promise<Response> {
    return this.fetch(`/calendar/stop/${conference}`, { method: 'POST' });
  },
  async joinConference(conference: string, params: JoinParams): Promise<Response> {
    return fetch(restApiPath() + `/calendar/join/${conference}`, {
      method: 'POST',
      body: JSON.stringify(params),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  async startConference(conference: string): Promise<Response> {
    return this.fetch(`/calendar/start/${conference}`, { method: 'POST' });
  },

  async testForSufficientBBBSlots(input: {
    id: string;
    maxAttendees: number;
    start: string;
    end: string;
    organization: string;
  }): Promise<
    {
      [uuid: string]: number;
    }[]
  > {
    const result = await this.post(`/bbb/check-limits`, JSON.stringify([input]));

    return await result.json();
  },

  clearMatrixTokens: async (): Promise<void> => {
    sessionStorage.clear();
    return;
  },

  getMatrixToken: async (force): Promise<MatrixTokenType | null> => {
    const claims = await getClaims();
    const prefix = claims?.sub ?? '';

    // force token renewal
    if (force) sessionStorage.removeItem(`${prefix}_${matrixTokenName}`);

    let tokenString = sessionStorage.getItem(`${prefix}_${matrixTokenName}`);

    if (!tokenString) {
      const tokens = await getTokens();
      const response = await fetch(`${restApiPath()}/matrix/token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokens?.access_token}` },
      });
      const token = await response.json();
      if (token && token.access_token && token.user_id && token.home_server) {
        tokenString = JSON.stringify(token);
        sessionStorage.setItem(`${prefix}_${matrixTokenName}`, tokenString);
      }
    }
    return tokenString ? JSON.parse(tokenString) : null;
  },

  createMatrixDm: async (uuid) => {
    const tokens = await getTokens();

    return fetch(`${restApiPath()}/matrix/dm/${uuid}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${tokens?.access_token}` },
    });
  },

  redactMatrixEvent: async (roomId: string, eventId: string) => {
    const tokens = await getTokens();

    return await fetch(`${restApiPath()}/matrix/action`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokens?.access_token}`,
      },
      body: JSON.stringify({
        action: 'redact',
        uuid: roomId,
        eventId: eventId,
      }),
    });
  },

  notify: async (
    message: Partial<NotificationType>,
    matrixInfos?: { roomId: string; groupName: string; client: MatrixClient },
  ): Promise<Response> => {
    if (matrixInfos) {
      const { roomId, groupName, client } = matrixInfos;
      const payload: RoomMessageEventContent = {
        msgtype: 'm.text' as MsgType.Text,
        body: JSON.stringify({
          ...message,
          groupName,
        }),
      };

      await client?.sendMessage(roomId, payload);
    }
    const body = JSON.stringify(message);

    const tokens = await getTokens();

    return await fetch(`${restApiPath()}/notify`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens?.access_token}`,
        'content-type': 'application/json',
      },
      body: body,
    });
  },
};
